import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { useGetSoftwareStatement } from "@/features/software-statement/api/get-software-statement";
import useQueryParams from "@/hooks/use-query-params";
import { getSafeRedirect } from "@/lib/utils";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { Label } from "@blueskydataplatform/shared-ui/components/ui/label";
import { useToast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useLogin, type LoginErrorData } from "../api/login";
import { useSend2FA } from "../api/send-2fa";
import { useReCaptcha } from "@blueskydataplatform/shared-ui/hooks/useReCaptcha";
import { AxiosError } from "axios";
import { loginSchema } from "../schemas/login-schema";
import Verification2FA from "./verification-2fa";
import { useMcpProvider } from "@/providers/mcp-providers-context";
import { clearStoredPromotionCode } from "@blueskydataplatform/shared-ui/store/use-order";
import { completeMcpAuthCallbackForProvider, reportMcpFailure } from "../complete-mcp-auth-callback";
import McpCallbackForm, { type McpCallbackHandle } from "../mcp-callback-form";
import Logo from "@/components/logo";
//import { useCheckDatasetsSubscription } from "@/features/subscriptions/api/check-datasets-subscription";
type LoginFormValues = z.infer<typeof loginSchema>;

const LoginForm = () => {
  const { provider } = useMcpProvider();
  const providerRef = useRef(provider);
  providerRef.current = provider;

  const [generalError, setGeneralError] = useState<string>();
  // Set once the server signals additional verification is needed (AAC-002);
  // subsequent submits attach an invisible reCAPTCHA token.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const { executeReCaptcha } = useReCaptcha({ action: "login" });
  const { toast } = useToast();
  const [twoFactorAuth, setTwoFactorAuth] = useState<{
    show: boolean;
    email: string;
    method: "phone" | "email";
  }>({
    show: false,
    email: "",
    method: "email",
  });

  const { redirect, isFromMCP } = useQueryParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const callbackRef = useRef<McpCallbackHandle>(null);

  const softwareStatementMutation = useGetSoftwareStatement();
  //const checkDatasetsSubscriptionMutation = useCheckDatasetsSubscription();
  const send2FAMutation = useSend2FA();
  const loginMutation = useLogin({
    mutationConfig: {
      onSuccess: async response => {
        const data = response.data;
        if (data.email) {
          setTwoFactorAuth({
            show: true,
            email: data.email,
            method: data.phone ? "phone" : "email",
          });
          send2FAMutation.mutate({ data: { email: data.email, method: data.phone ? "phone" : "email" } });
          return;
        }

        clearStoredPromotionCode();

        if (isFromMCP) {
          try {
            await completeMcpAuthCallbackForProvider({
              getProvider: () => providerRef.current,
              fetchSoftwareStatement: input => softwareStatementMutation.mutateAsync(input),
              callbackRef,
            });
          } catch (error) {
            reportMcpFailure(error, message => toast.error(message));
          }

          return;
        }

        // Only honor the `r` query param if it is a safe same-origin/relative
        // target — prevents open redirect to attacker-controlled domains (IH-004).
        const safeRedirect = getSafeRedirect(redirect);
        if (safeRedirect) {
          window.location.href = safeRedirect;
          return;
        }

        if (data.redirect) {
          window.location.href = data.redirect;
          return;
        }
      },
      onError: (error: unknown) => {
        const data = (error as AxiosError<LoginErrorData>).response?.data;

        if (data?.lockout) {
          const minutes = data.retryAfter ? Math.ceil(data.retryAfter / 60) : 15;
          const message = `Too many failed attempts. Please try again in about ${minutes} minute${
            minutes === 1 ? "" : "s"
          }.`;
          setGeneralError(message);
          toast.error(message);
          return;
        }

        if (data?.captchaRequired) {
          setCaptchaRequired(true);
          const message = "Additional verification is required. Please try signing in again.";
          setGeneralError(message);
          toast.error(message);
          return;
        }

        setGeneralError("Login failed. Please check your credentials.");
        toast.error("Login failed. Please check your credentials.");
      },
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setGeneralError(undefined);
    // Once the server has asked for verification, attach an invisible reCAPTCHA
    // token (best-effort: a null token still submits and lets the server decide).
    let recaptchaToken: string | undefined;
    if (captchaRequired) {
      recaptchaToken = (await executeReCaptcha("login")) || undefined;
    }
    loginMutation.mutate({ data: { ...data, recaptchaToken } });
  };

  if (twoFactorAuth.show) {
    return <Verification2FA email={twoFactorAuth.email} method={twoFactorAuth.method} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          {/* Logo placeholder */}
          <div className="flex justify-center mb-4 pb-4">
            <Logo className="h-8" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Sign in</CardTitle>
          <CardDescription className="text-center">
            Enter your email and password to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <McpCallbackForm handleRef={callbackRef} />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {generalError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {generalError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register("email")}
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <UILink preserveQuery to={paths.passwordReset.getHref()} className="text-xs">
                  Forgot password?
                </UILink>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                {...register("password")}
                className={errors.password ? "border-red-500" : ""}
              />
              {errors.password && <p className="text-sm text-red-600">{errors.password.message}</p>}
            </div>

            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {/* Register link */}
          <div className="mt-6 border-t border-border pt-6 text-center">
            <span className="text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <UILink preserveQuery to={paths.register.getHref()} className="text-sm font-semibold">
                Sign up
              </UILink>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginForm;
